import { BasicTabProps } from "@components/common/tabs/tabs-type";
import { getVerticalTabsStyles } from "@components/common/tabs/vertical-tabs-style";
import { Grid2 } from "@mui/material";
import { BaseSettingsPage } from "@pages/settings/base-settings-page";
import { neutral } from "@flat-ui";
import {
  DEFAULT_BOLD_TEXT_FONT_WEIGHT,
  DEFAULT_INPUT_FONT_SIZE,
  DEFAULT_TEXT_FONT_SIZE,
} from "@styles/common-styles";
import { OpenTabEvents } from "@utils/track-event/track-event-list";
import { TabItem } from "@components/common/tabs/tab-item";

interface Props<K extends string> {
  /** List of available tabs */
  tabs: Record<K, BasicTabProps<K>>;

  /** The header to show on the tab selection section. If not available, nothing is being shown */
  tabSelectionHeader?: string;

  /** Flag whether something is being load or not */
  isLoading: boolean;

  /** The selected tab */
  selectedTabId: K;

  /**
   * The name of the event to track opening tab events.
   * In the same page all opening of a tab is tracked with the same event name,
   * and the tab name is sent as a prop.
   */
  trackingEventPageName: OpenTabEvents;

  /** The data test id for the button */
  dataTestId: string;
}

/**
 * A general component which generates vertical tabs.
 * The MUI vertical tabs are not used because it is unnecessary complicated for our purpose
 */
export function VerticalTabs<K extends string>({
  tabs,
  tabSelectionHeader,
  isLoading,
  selectedTabId,
  trackingEventPageName,
  dataTestId,
}: Props<K>): JSX.Element {
  const selectedTab = tabs[selectedTabId];
  const { tabListSection, tabContentSection } = getVerticalTabsStyles();

  return (
    <Grid2 container>
      {/* The header for both tab label and content */}
      <Grid2 container alignItems="flex-end" flexWrap="nowrap">
        {/* The header for tab label */}
        <Grid2
          sx={{
            borderBottom: `1px solid ${neutral[200]}`,
            pb: "10px",
            mr: tabListSection.mr,
            ml: "0px",
            width: tabListSection.width,
            fontSize: DEFAULT_TEXT_FONT_SIZE,
            letterSpacing: "0.48px",
            textTransform: "uppercase",
            fontWeight: "700",
          }}
        >
          {tabSelectionHeader}
        </Grid2>

        {/* The header for tab content */}
        <Grid2
          sx={{
            borderBottom: `1px solid ${neutral[200]}`,
            pb: "10px",
            width: tabContentSection.width,
            mr: "0px",
            fontSize: "24px",
            fontWeight: DEFAULT_BOLD_TEXT_FONT_WEIGHT,
          }}
        >
          {selectedTab.label}
        </Grid2>
      </Grid2>

      <Grid2 container sx={{ mt: "40px" }} flexWrap="nowrap">
        {/* The tab label */}
        <Grid2
          sx={{
            mr: tabListSection.mr,
            ml: "0px",
            width: tabListSection.width,
            fontSize: DEFAULT_INPUT_FONT_SIZE,
            textTransform: "uppercase",
            fontWeight: "700",
          }}
        >
          {Object.values<BasicTabProps<K>>(tabs).map((tab) => (
            <TabItem
              key={tab.label}
              label={tab.label}
              routeId={tab.routeId}
              isHidden={tab.isHidden}
              content={tab.content}
              isSelected={selectedTab.label === tab.label}
              trackingEventPageName={trackingEventPageName}
            />
          ))}
        </Grid2>

        {/* The tab content */}
        <Grid2
          sx={{
            width: tabContentSection.width,
            mr: "0px",
          }}
        >
          <BaseSettingsPage
            pageSectionProps={selectedTab.content}
            isLoading={isLoading}
            dataTestId={dataTestId}
          />
        </Grid2>
      </Grid2>
    </Grid2>
  );
}
